import { useTranslation } from 'react-i18next';
import { useLots } from '../../../lib/lots-api';
import { usePscList } from '../../../lib/psc-api';
import { useLnpList } from '../../../lib/lnp-api';
import { useMpgList } from '../../../lib/mpg-api';
import { useSpedizioniList } from '../../../lib/spedizioni-api';
import { useInvList } from '../../../lib/inv-api';
import { useSapList } from '../../../lib/sap-api';
import { useStockMovements } from '../../../lib/stock-movements-api';
import { useCurrentPeriod } from '../../../lib/periods-api';

function StatBox({ label, value, accent }: { label: string; value: string | number; accent?: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${accent ?? 'text-slate-900'}`}>{value}</p>
    </div>
  );
}

export function OverviewKpiWidget() {
  const { t } = useTranslation();
  const periodQ = useCurrentPeriod();
  // pageSize alto per evitare che la sum grams sotto stimi i lot oltre il pageSize
  // (bug riscontrato post-migration import: 215 lot vs pageSize 200 mostrava -190kg).
  const lotsQ = useLots({ status: 'ACTIVE', pageSize: 2000 });
  const pscQ = usePscList({ status: 'DRAFT', pageSize: 1 });
  const lnpQ = useLnpList({ status: 'DRAFT', pageSize: 1 });
  const mpgQ = useMpgList({ status: 'DRAFT', pageSize: 1 });
  // Spedizioni (MSS + MSF): un'unica query, total conta entrambe le destinazioni.
  const spedQ = useSpedizioniList({ status: 'DRAFT', pageSize: 1 });
  const invQ = useInvList({ status: 'DRAFT', pageSize: 1 });
  const sapQ = useSapList({ status: 'DRAFT', pageSize: 1 });
  const movsQ = useStockMovements(
    { periodId: periodQ.data?.id, pageSize: 1 },
    !!periodQ.data?.id,
  );

  const totLots = lotsQ.data?.total ?? 0;
  const totGrams = (lotsQ.data?.items ?? []).reduce((s, l) => s + Number(l.residualQty), 0);
  const totDraft =
    (pscQ.data?.total ?? 0) +
    (lnpQ.data?.total ?? 0) +
    (mpgQ.data?.total ?? 0) +
    (spedQ.data?.total ?? 0) +
    (invQ.data?.total ?? 0) +
    (sapQ.data?.total ?? 0);
  const totMovs = movsQ.data?.total ?? 0;

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatBox label={t('dashboard.overview.totLots')} value={totLots} accent="text-emerald-600" />
      <StatBox
        label={t('dashboard.overview.totGrams')}
        value={`${(totGrams / 1000).toFixed(1)} kg`}
        accent="text-emerald-600"
      />
      <StatBox label={t('dashboard.overview.totDraft')} value={totDraft} accent="text-amber-600" />
      <StatBox label={t('dashboard.overview.totMovementsMonth')} value={totMovs} accent="text-brand-700" />
    </div>
  );
}
